'use client';

import { useContext, useEffect, useId, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { equals, path } from 'ramda';
import { FormProvider, useForm } from 'react-hook-form';
import { tv } from 'tailwind-variants';
import { Button, Form, Separator } from '../components';
import { useIsMobile } from '../hooks';
import { DeclarativeFormField } from './DeclarativeFormField';
import { FormDirtyContext } from './FormDirtyContext';
import { mergeFormConfig, useFormConfig } from './FormConfigContext';
import { getFieldsRequiredStatus } from './utils/requiredField';
import { deleteAtPath, isFieldVisible, makeFieldsOptionalInSchema, partitionFieldsByVisibility, stripHiddenFieldValues } from './utils/visibility';
import type { DefaultValues, FieldValues, Path, Resolver } from 'react-hook-form';
import type { VariantProps } from 'tailwind-variants';
import type { FormConfig } from './FormConfigContext';
import type { DeclarativeFormConfig, DeclarativeFormProps } from './types';
import type { z } from 'zod';
const declarativeForm = tv({
  slots: {
    base: `
      flex
      flex-col
      w-full
    `,
    rows: `
      flex
      flex-row
      items-start
      gap-4
    `,
    actions: `
      flex
      justify-end
      gap-2
    `
  },
  variants: {
    rowGap: {
      sm: {
        base: 'gap-2'
      },
      md: {
        base: 'gap-4'
      },
      lg: {
        base: 'gap-6'
      }
    },
    inputGap: {
      sm: {
        rows: 'gap-2'
      },
      md: {
        rows: 'gap-4'
      },
      lg: {
        rows: 'gap-6'
      }
    },
    actionsGap: {
      sm: {
        actions: 'gap-2'
      },
      md: {
        actions: 'gap-4'
      },
      lg: {
        actions: 'gap-6'
      }
    },
    actionsPosition: {
      left: {
        actions: 'justify-start'
      },
      right: {
        actions: 'justify-end'
      }
    }
  },
  defaultVariants: {
    size: 'default',
    rowGap: 'md',
    inputGap: 'md',
    actionsGap: 'md',
    actionsPosition: 'right'
  }
});

// Default form configuration values
const FORM_CONFIG_DEFAULTS: Required<FormConfig> = {
  rowGap: 'md',
  inputGap: 'md',
  actionsGap: 'md',
  labelSize: 'md',
  submitButtonText: 'Submit',
  submitButtonTextPending: 'Submitting...',
  showResetButton: false,
  showSeparator: true,
  resetButtonText: 'Reset'
};
const snakeToCamel = (value: string): string => value.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());

/**
 * Maps an API error path (e.g. `account.country`) to a form field name: explicit
 * override, else exact match, else the camelCased leaf (`account.account_number` ->
 * `accountNumber`). Unmatched paths are returned as-is so the error falls back to the
 * toast instead of attaching to the wrong field.
 */
export const resolveServerErrorPath = (apiPath: string, fieldNames: readonly string[], overrides?: Record<string, string>): string => {
  const override = overrides?.[apiPath];
  if (override) return override;
  if (fieldNames.includes(apiPath)) return apiPath;
  const leaf = apiPath.split('.').pop() ?? apiPath;
  const camelLeaf = snakeToCamel(leaf);
  if (camelLeaf !== apiPath && fieldNames.includes(camelLeaf)) return camelLeaf;
  return apiPath;
};

/**
 * Splits API field errors into those that resolve to an unconditionally rendered form field
 * and those that don't. Fields with `hidden` or `shouldRender` count as unmatched because
 * live form values are unavailable here. Unmatched errors keep their original API path.
 */
export const partitionServerErrors = <TFieldValues extends FieldValues,>(serverErrors: Record<string, string>, config: Pick<DeclarativeFormConfig<TFieldValues>, 'rows' | 'serverErrorFieldMap'>): {
  matched: Record<string, string>;
  unmatched: Record<string, string>;
} => {
  const fields = config.rows.flat();
  const fieldNames: readonly string[] = fields.map(field => field.name);
  const alwaysRenderedNames = new Set<string>(fields.filter(field => !field.hidden && !field.shouldRender).map(field => field.name));
  const matched: Record<string, string> = {};
  const unmatched: Record<string, string> = {};
  Object.entries(serverErrors).forEach(([apiPath, message]) => {
    const fieldPath = resolveServerErrorPath(apiPath, fieldNames, config.serverErrorFieldMap);
    if (alwaysRenderedNames.has(fieldPath)) {
      matched[apiPath] = message;
    } else {
      unmatched[apiPath] = message;
    }
  });
  return {
    matched,
    unmatched
  };
};
export function DeclarativeForm<TFieldValues extends FieldValues>({
  classNames,
  config,
  context,
  formProps,
  rowGap: rowGapProp,
  inputGap: inputGapProp,
  actionsGap: actionsGapProp,
  onSubmit,
  onError,
  submitButtonText: submitButtonTextProp,
  submitButtonTextPending: submitButtonTextPendingProp,
  onCancel,
  cancelButtonText = 'Cancel',
  isPending = false,
  markSubmittedOnSubmitSuccess = true,
  labelSize: labelSizeProp,
  showResetButton: showResetButtonProp,
  showSeparator: showSeparatorProp,
  resetButtonText: resetButtonTextProp,
  actionsComponent,
  formId,
  onDirtyChange,
  onValuesChange,
  resetOnSuccess = false,
  serverErrors,
  uploadProps,
  getValuesRef,
  beforeFields
}: DeclarativeFormProps<TFieldValues> & VariantProps<typeof declarativeForm>) {
  // Merge explicit props with context and defaults
  const contextConfig = useFormConfig();
  const {
    rowGap,
    inputGap,
    actionsGap,
    labelSize,
    submitButtonText,
    submitButtonTextPending,
    showResetButton,
    showSeparator,
    resetButtonText
  } = mergeFormConfig({
    rowGap: rowGapProp,
    inputGap: inputGapProp,
    actionsGap: actionsGapProp,
    labelSize: labelSizeProp,
    submitButtonText: submitButtonTextProp,
    submitButtonTextPending: submitButtonTextPendingProp,
    showResetButton: showResetButtonProp,
    showSeparator: showSeparatorProp,
    resetButtonText: resetButtonTextProp
  }, contextConfig, FORM_CONFIG_DEFAULTS);
  const flatFields = useMemo(() => {
    return config.rows.flat();
  }, [config.rows]);

  // Hoist to avoid reconstructing the resolver on every validation call (which happens
  // per keystroke after a failed submit, since reValidateMode defaults to onChange).
  const baseZodResolver = useMemo(() => {
    // TODO: remove this once we have a better way to handle the zod resolver
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return zodResolver(config.schema as any);
  }, [config.schema]);

  // Cache for the visibility-derived resolver used in the "treat as pass" fallback.
  // Keyed on the sorted hidden-field names AND the schema reference — rebuilt when either
  // visibility changes or the schema is replaced (e.g. dynamic forms).
  const visibilityResolverCacheRef = useRef<{
    key: string;
    schema: z.ZodType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: Resolver<any>;
  } | null>(null);

  // Strips errors on hidden fields, then — if all errors were stripped but zod still
  // returned no parsed values (its failure-path behaviour) — re-runs validation against a
  // visibility-derived schema that makes hidden required fields optional, so coercions and
  // transforms (e.g. z.preprocess string→number) still run on the visible fields.
  /* eslint-disable @typescript-eslint/no-explicit-any -- resolver signature matches react-hook-form's Resolver<any> */
  const resolveWithHiddenFieldsSkipped = async (values: any, resolverContext: any, options: any) => {
    const result = await baseZodResolver(values, resolverContext, options);
    const errors = result.errors as Record<string, unknown>;
    const {
      hiddenNames
    } = partitionFieldsByVisibility(flatFields, values, undefined);
    hiddenNames.forEach(name => deleteAtPath(errors, name));

    // zodResolver returns { values: {}, errors: {...} } on failure and
    // { values: <parsed>, errors: {} } on success. If we stripped all errors but
    // values is still empty (zodResolver never populated it), re-parse with a
    // visibility-derived schema so hidden required fields are optional and zod
    // returns properly coerced/transformed values (e.g. z.preprocess string→number).
    if (Object.keys(errors).length === 0 && Object.keys(result.values as object).length === 0) {
      const key = [...hiddenNames].sort().join(',');
      let resolverCache = visibilityResolverCacheRef.current;
      if (!resolverCache || resolverCache.key !== key || resolverCache.schema !== config.schema) {
        const visibilitySchema = makeFieldsOptionalInSchema(config.schema, [...hiddenNames]);
        resolverCache = {
          key,
          schema: config.schema,
          resolver: zodResolver(visibilitySchema as any)
        };
        visibilityResolverCacheRef.current = resolverCache;
      }
      return resolverCache.resolver(values, resolverContext, options);
    }
    return result;
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const form = useForm<TFieldValues, typeof context>({
    resolver: resolveWithHiddenFieldsSkipped,
    defaultValues: config.defaultValues as DefaultValues<TFieldValues> | undefined,
    context
  });
  if (getValuesRef) getValuesRef.current = form.getValues as () => FieldValues;
  const fieldNames = useMemo(() => {
    return flatFields.map(fieldConfig => fieldConfig.name);
  }, [flatFields]);
  const requiredFieldsMap = useMemo(() => {
    return getFieldsRequiredStatus(config.schema, fieldNames);
  }, [config.schema, fieldNames]);
  const currentValues = form.watch();
  const {
    isDirty,
    dirtyFields
  } = form.formState;
  // Combine isDirty (value comparison) with dirtyFields (user interaction tracking)
  // so programmatic changes (e.g. auto-select) don't trigger dirty state
  const hasUserChanges = isDirty && Object.keys(dirtyFields).length > 0;
  const {
    base,
    rows,
    actions
  } = declarativeForm({
    rowGap,
    inputGap,
    actionsGap
  });
  const isMobile = useIsMobile();
  const generatedFormId = useId();
  const dirtyFormId = formId ?? generatedFormId;
  const formDirtyContext = useContext(FormDirtyContext);
  useEffect(() => {
    onDirtyChange?.(hasUserChanges);
    formDirtyContext?.reportDirty(dirtyFormId, hasUserChanges);
  }, [dirtyFormId, formDirtyContext, hasUserChanges, onDirtyChange]);
  useEffect(() => {
    if (!formDirtyContext) return;
    return form.subscribe({
      formState: {
        dirtyFields: true,
        isDirty: true,
        values: true
      },
      callback: ({
        dirtyFields: nextDirtyFields = {},
        isDirty: nextIsDirty
      }) => {
        formDirtyContext.reportDirty(dirtyFormId, Boolean(nextIsDirty) && Object.keys(nextDirtyFields).length > 0);
      }
    });
  }, [dirtyFormId, form, formDirtyContext]);

  // react-hook-form requires reset() to run after the submit lifecycle (in an
  // effect keyed on isSubmitSuccessful), not synchronously inside the handler.
  const {
    isSubmitSuccessful
  } = form.formState;
  useEffect(() => {
    if (markSubmittedOnSubmitSuccess && isSubmitSuccessful) {
      formDirtyContext?.reportSubmitted(dirtyFormId);
    }
    if (resetOnSuccess && isSubmitSuccessful) {
      form.reset(config.defaultValues as DefaultValues<TFieldValues> | undefined);
    }
  }, [config.defaultValues, dirtyFormId, form, formDirtyContext, isSubmitSuccessful, markSubmittedOnSubmitSuccess, resetOnSuccess]);

  // Track previous values to detect actual changes for onValuesChange
  const prevValuesRef = useRef<string>(JSON.stringify(config.defaultValues ?? ''));
  const hasInteracted = useRef(false);
  const isRevertingRef = useRef(false);
  useEffect(() => {
    if (hasUserChanges) hasInteracted.current = true;
  }, [hasUserChanges]);
  useEffect(() => {
    if (!onValuesChange || !hasInteracted.current) return;
    // Skip changes triggered by a revert (form.reset)
    if (isRevertingRef.current) {
      isRevertingRef.current = false;
      return;
    }
    const serialized = JSON.stringify(currentValues);
    if (serialized === prevValuesRef.current) return;

    // Optimistically update prevValuesRef before calling onValuesChange to
    // prevent re-entrant calls caused by intermediate re-renders (e.g. when
    // onValuesChange triggers state updates that give onValuesChange a new
    // reference before the promise resolves).
    const previousSerialized = prevValuesRef.current;
    prevValuesRef.current = serialized;
    const handleResult = (result_0: void | false) => {
      if (result_0 === false) {
        // Callback rejected — revert to the latest known-good state
        prevValuesRef.current = previousSerialized;
        if (previousSerialized) {
          isRevertingRef.current = true;
          form.reset(JSON.parse(previousSerialized) as TFieldValues);
        }
      }
    };

    // Validate against the zod schema before calling onValuesChange
    form.trigger().then(isValid => {
      if (!isValid) {
        // Validation failed — restore previous ref so the change can be retried
        prevValuesRef.current = previousSerialized;
        return;
      }
      const result_1 = onValuesChange(currentValues);
      if (result_1 instanceof Promise) {
        result_1.then(handleResult, () => handleResult(false));
      } else {
        handleResult(result_1);
      }
    });
  }, [currentValues, onValuesChange, form]);

  // Track which fields have server errors so we can clear them when serverErrors changes
  const serverErrorFieldsRef = useRef<string[]>([]);

  // Set server-side validation errors on form fields
  useEffect(() => {
    // Clear previous server errors first
    serverErrorFieldsRef.current.forEach(fieldPath => {
      form.clearErrors(fieldPath as Path<TFieldValues>);
    });
    if (serverErrors) {
      const resolvedPaths: string[] = [];
      Object.entries(serverErrors).forEach(([apiPath, message]) => {
        const fieldPath_0 = resolveServerErrorPath(apiPath, fieldNames, config.serverErrorFieldMap);
        resolvedPaths.push(fieldPath_0);
        form.setError(fieldPath_0 as Path<TFieldValues>, {
          type: 'server',
          message
        });
      });
      serverErrorFieldsRef.current = resolvedPaths;
    } else {
      serverErrorFieldsRef.current = [];
    }
  }, [serverErrors, form, fieldNames, config.serverErrorFieldMap]);

  // Flatten rows for mobile to ensure single column layout
  const processedRows = useMemo(() => {
    if (isMobile) {
      return flatFields.map(field => [field]);
    }
    return config.rows;
  }, [config.rows, flatFields, isMobile]);

  // Clear form values whose every claiming config is currently hidden. Multiple configs may
  // share a `name` (e.g. relabeling one value based on a sibling selection), so cleanup is
  // name-scoped — a name survives as long as any config claiming it is visible.
  // resetField is used instead of setValue(undefined) to keep controlled inputs from
  // transitioning to uncontrolled, which would leave the stale DOM value visible when
  // the field reappears.
  useEffect(() => {
    const {
      hiddenNames: hiddenNames_0
    } = partitionFieldsByVisibility(flatFields, currentValues, form);
    const defaults = (form.formState.defaultValues ?? {}) as Record<string, unknown>;
    hiddenNames_0.forEach(name_0 => {
      const fieldName = name_0 as Path<TFieldValues>;
      if (equals(form.getValues(fieldName), path(name_0.split('.'), defaults))) return;
      form.resetField(fieldName);
    });
  }, [currentValues, flatFields, form]);
  return <FormProvider {...form}>
      <Form {...form}>
        <form className={base({
        className: classNames?.form
      })} key={formId} onSubmit={form.handleSubmit((values_0, event) => {
        let cleaned = stripHiddenFieldValues(values_0, flatFields, form);
        if (config.convertEmptyToNull || config.omitEmpty) {
          cleaned = {
            ...cleaned
          };
          for (const [key_0, value] of Object.entries(cleaned)) {
            if (value === '' || value === undefined) {
              if (config.omitEmpty) {
                delete cleaned[key_0];
              } else {
                cleaned[key_0] = null;
              }
            }
          }
        }
        return onSubmit(cleaned as TFieldValues, event);
      }, onError)} {...formProps}>
          {beforeFields}
          {processedRows.map((row, rowIndex) => {
          // Check if any field in this row should be rendered
          const hasVisibleFields = row.some(fieldConfig_0 => !fieldConfig_0.hidden && isFieldVisible(fieldConfig_0, currentValues, form));

          // Detect names that appear more than once in this row (shared-name pattern)
          const nameCounts = new Map<string, number>();
          row.forEach(fc => nameCounts.set(String(fc.name), (nameCounts.get(String(fc.name)) ?? 0) + 1));
          return <div className={rows({
            className: classNames?.rows
          })} hidden={!hasVisibleFields} key={`row-${rowIndex}`}>
                {row.map((fieldConfig_1, fieldIndex) => {
              if (fieldConfig_1.hidden) return null;
              const isVisible = isFieldVisible(fieldConfig_1, currentValues, form);
              const isRequired = requiredFieldsMap.get(fieldConfig_1.name);
              const isSharedName = (nameCounts.get(String(fieldConfig_1.name)) ?? 0) > 1;

              // Shared-name fields: only one Controller can be mounted at a time
              if (isSharedName) {
                if (!isVisible) return null;
                return <DeclarativeFormField control={form.control} fieldConfig={{
                  required: isRequired,
                  ...fieldConfig_1
                }} key={`${String(fieldConfig_1.name)}-${fieldIndex}`} labelSize={labelSize} uploadProps={uploadProps} />;
              }

              // Unique-name fields: stay mounted, hidden via attribute
              return <div className="flex-1" hidden={!isVisible} key={String(fieldConfig_1.name)}>
                      <DeclarativeFormField control={form.control} fieldConfig={{
                  required: isRequired,
                  ...fieldConfig_1
                }} labelSize={labelSize} uploadProps={uploadProps} />
                    </div>;
            })}
              </div>;
        })}
          {showSeparator && <Separator />}
          <div className={actions({
          className: classNames?.actions
        })}>
            {actionsComponent ? actionsComponent(form.formState) : <>
                {showResetButton && <Button disabled={form.formState.isSubmitting} onClick={() => form.reset(config.defaultValues as DefaultValues<TFieldValues> | undefined)} type="button" variant="quaternary">
                    {resetButtonText}
                  </Button>}
                {onCancel && <Button disabled={form.formState.isSubmitting} onClick={onCancel} type="button" variant="secondary">
                    {cancelButtonText}
                  </Button>}
                <Button className={classNames?.button} disabled={form.formState.isSubmitting || isPending} type="submit">
                  {form.formState.isSubmitting ? submitButtonTextPending : submitButtonText}
                </Button>
              </>}
          </div>
        </form>
      </Form>
    </FormProvider>;
}